import { api } from '@/api'
import AButton from '@/components/A/AButton/AButton'
import AIcon from '@/components/A/AIcon/AIcon'
import { ApplyTrialParam } from '@/components/ApplyTrialBox/ApplyTrialBox'
import MiddleText from '@/components/MiddleText/MiddleText'
import FreeTrialModal from '@/components/Modals/FreeTrialModal/FreeTrialModal'
import useNewNavigate from '@/helpers/commonHooks/useNewNavigate'
import useProductType from '@/helpers/commonHooks/useProductType'
import { getProductDashboardLink } from '@/router/pageConst'
import { corpService } from '@/services/corpService'
import { userService } from '@/services/userService'
import { isFreeTrialProductCategory } from '@/helpers/productCategory'
import NiceModal from '@ebay/nice-modal-react'
import { message } from 'antd'
import { observer } from 'mobx-react'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'


interface Props {
  as?: React.ReactNode
}

function ProjectCreateBtn(props: Props) {
  const { as } = props
  const navigate = useNewNavigate()
  const { productType } = useProductType()
  const { t } = useTranslation()
  const trialStatus = corpService.getFreeTrialStatus(productType)
  const prevLoadedCanUseFreeTrialRef = useRef(false)
  const canUseFreeTrial = isFreeTrialProductCategory(productType) && trialStatus.freeTrial
  const displayCanUseFreeTrial = trialStatus.loaded
    ? canUseFreeTrial
    : prevLoadedCanUseFreeTrialRef.current
  const needApplyFreeTrial = canUseFreeTrial && !trialStatus.hasApply

  useEffect(() => {
    if (trialStatus.loaded) {
      prevLoadedCanUseFreeTrialRef.current = canUseFreeTrial
    }
  }, [canUseFreeTrial, trialStatus.loaded])

  useEffect(() => {
    corpService.refreshFreeTrialStatus(productType)
  }, [productType])

  const handleBtnClick = () => {
    if (!trialStatus.loaded) {
      return
    }
    if (needApplyFreeTrial) {
      showFreeTrialModal()
    } else {
      navigate(getProductDashboardLink(productType, 'projectCreate'))
    }
  }

  const showFreeTrialModal = () => {
    NiceModal.show(FreeTrialModal, {
      userInfo: userService.userInfo!,
      onSubmit: async (param: ApplyTrialParam) => {
        try {
          await api.corp.applyForFreeTrial(param).send()
          corpService.refreshFreeTrialStatus(productType, { force: true })
          navigate(getProductDashboardLink(productType, 'projectCreate'))
          message.success(t('modals.freeTrialModal.message.submitSuccess'))
        } catch (e: any) {
          const error = e as ApiError
          message.error(error.statusMessage)
        }
      }
    })
  }

  return <span onClick={handleBtnClick} data-testid='project-create-entry-btn'>
    {as || <DefaultBtn canUseFreeTrial={displayCanUseFreeTrial} />}
  </span>
}



const DefaultBtn = observer((props: { canUseFreeTrial?: boolean }) => {
  const { t } = useTranslation()
  return <AButton testId='project-create-btn' type='primary' size='LG-AUTO' >
    <AIcon type='icon-line-global_add'></AIcon>
    {
      props.canUseFreeTrial
        ? <MiddleText text={t('modals.freeTrialModal.title1')} />
        : <MiddleText text={t('dashboard.projectPage.createProject')} />
    }
  </AButton>
})



export default observer(ProjectCreateBtn)
